import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Prohibit } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Switch } from "../components/ui/switch";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/batches", label: "Lead Batches", icon: <Prohibit size={18} weight="duotone" />, testId: "nav-admin-lead-batches" }];

const CATEGORY_LABELS = { trust: "Trust & Asset Protection", funding: "Business Funding" };
const CATEGORY_TABS = [{ key: "all", label: "All" }, { key: "trust", label: "Trust" }, { key: "funding", label: "Funding" }];

export default function AdminLeadBatchesPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [categoryTab, setCategoryTab] = useState("all");

  const fetchBatches = async () => {
    try {
      const { data } = await api.get("/admin/lead-batches");
      setBatches(data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const toggleFreeze = async (batch) => {
    const nextFrozen = !batch.frozen;
    if (nextFrozen && !window.confirm(`Freeze "${batch.batch_label}"? Its ${batch.untouched_count} untouched lead(s) will be hidden from reps going forward. Nothing is deleted.`)) return;
    setTogglingId(batch.id);
    try {
      const { data } = await api.patch(`/admin/lead-batches/${batch.id}/freeze`, { frozen: nextFrozen });
      toast.success(data.message);
      fetchBatches();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  const closeOutBatch = async (batch) => {
    if (!window.confirm(`Permanently close out "${batch.batch_label}"? This removes ALL ${batch.lead_count} lead(s) in this batch from every rep's queue — including ones already touched. This cannot be undone from here. Continue?`)) return;
    setClosingId(batch.id);
    try {
      const { data } = await api.post(`/admin/lead-batches/${batch.id}/close-out`);
      toast.success(data.message);
      fetchBatches();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClosingId(null);
    }
  };

  const visibleBatches = categoryTab === "all" ? batches : batches.filter((b) => b.category === categoryTab);

  return (
    <DashboardLayout navItems={navItems} moduleName="Lead Batches" moduleBackPath="/admin">
      <div data-testid="admin-lead-batches-page">
        <div className="mb-6">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every manual CSV lead upload, one row per upload event. Freeze to pause instantly, Close Out to permanently remove — nothing is ever deleted from the database.">Batch Manager — Kill Switch</h1>
          <p className="text-igc-muted text-sm">Freeze any single CSV upload to instantly pause it across all reps (touched leads stay visible), or permanently Close Out a batch to remove it from every queue entirely. Works independently per category — freeze Trust while Funding stays live, or vice versa.</p>
        </div>

        <div className="flex gap-2 mb-4">
          {CATEGORY_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`lead-batches-tab-${t.key}`}
              onClick={() => setCategoryTab(t.key)}
              className={`text-xs px-3 py-1.5 border uppercase tracking-widest ${categoryTab === t.key ? "border-igc-gold text-igc-goldText bg-igc-gold/10" : "border-igc-gold/20 text-igc-muted hover:text-igc-cream"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="lead-batches-loading">Loading...</p>}
        {!loading && error && <p className="text-igc-danger text-sm" data-testid="lead-batches-error-state">Couldn't load lead batches. Please refresh the page or try again shortly.</p>}
        {!loading && !error && visibleBatches.length === 0 && (
          <p className="text-igc-muted text-sm" data-testid="lead-batches-empty-state">No manual CSV uploads in this category yet.</p>
        )}

        {!loading && !error && visibleBatches.length > 0 && (
          <div className="bg-igc-surface border border-igc-gold/15 overflow-x-auto">
            <table className="w-full text-sm" data-testid="lead-batches-table">
              <thead>
                <tr className="border-b border-igc-gold/15 text-igc-muted text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Batch</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Rep</th>
                  <th className="text-left px-4 py-3">Uploaded By</th>
                  <th className="text-left px-4 py-3">Leads (Touched / Untouched)</th>
                  <th className="text-left px-4 py-3">Uploaded</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Close Out</th>
                </tr>
              </thead>
              <tbody>
                {visibleBatches.map((b) => (
                  <tr key={b.id} className="border-b border-igc-gold/10" data-testid={`lead-batch-row-${b.id}`}>
                    <td className="px-4 py-3 text-igc-cream">{b.batch_label}</td>
                    <td className="px-4 py-3 text-igc-muted">{CATEGORY_LABELS[b.category] || b.category}</td>
                    <td className="px-4 py-3 text-igc-muted">{b.rep_name || "Unassigned Pool"}</td>
                    <td className="px-4 py-3 text-igc-muted">{b.uploaded_by_name} ({b.uploaded_by_role})</td>
                    <td className="px-4 py-3 text-igc-muted" data-testid={`lead-batch-counts-${b.id}`}>{b.lead_count} ({b.touched_count} / {b.untouched_count})</td>
                    <td className="px-4 py-3 text-igc-muted">{formatET(b.created_at, "MMM d, yyyy h:mm a")}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          data-testid={`lead-batch-freeze-toggle-${b.id}`}
                          checked={!!b.frozen}
                          disabled={togglingId === b.id || !!b.closed_out}
                          onCheckedChange={() => toggleFreeze(b)}
                          title="Freezes/unfreezes this batch — frozen leads not yet touched by a rep are hidden going forward."
                        />
                        {b.closed_out ? (
                          <span className="text-igc-danger text-xs uppercase tracking-widest" data-testid={`lead-batch-closed-out-badge-${b.id}`}>Closed Out</span>
                        ) : b.frozen ? (
                          <span className="text-igc-danger text-xs uppercase tracking-widest" data-testid={`lead-batch-frozen-badge-${b.id}`}>Frozen</span>
                        ) : (
                          <span className="text-igc-muted text-xs uppercase tracking-widest">Active</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {b.closed_out ? (
                        <span className="text-igc-muted text-xs">—</span>
                      ) : (
                        <button
                          data-testid={`lead-batch-close-out-button-${b.id}`}
                          onClick={() => closeOutBatch(b)}
                          disabled={closingId === b.id}
                          title="Permanently removes this batch from every rep's queue, including leads already touched. Requires confirmation. Cannot be undone."
                          className="text-xs px-2.5 py-1 border border-igc-danger/40 text-igc-danger hover:bg-igc-danger/10 transition-colors disabled:opacity-50"
                        >
                          {closingId === b.id ? "Closing…" : "Close Out"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
